/**
 * Nova junção — o assistente (Q9). O operador diz a junção em termos do carro: o
 * componente onde ela fica, a base do nome do fio, e os ramos — cada um com o
 * pino na junção e a ponta distante (um pino que já está no desenho, ou um novo).
 *
 * **Ninguém digita `#`.** Cada ramo vira os dois fios da convenção da equipe,
 * `BASE.RAMO` na ponta e `BASE.RAMO#` na junção (Q11), e o componente vira
 * Junção. A tabela mostra os nomes antes de ir para o lote. Ponta que já existe
 * com outro nome leva o fio dela para `BASE.RAMO`, com as pontas que vão junto
 * (Q17) — o servidor lista quais no resultado.
 *
 * Nada é gravado aqui: o botão põe UMA operação `criarJuncao` no lote. O laço
 * (duas pontas que já se ligam) é recusado na hora, como no resto do editor.
 */

import { useState } from "react"
import { usarChicotes, usarConectores, usarCores } from "@/api/catalogos"
import { usarBuscaDeComponentes, type GrafoDoPinout, type NoDoGrafo, type OperacaoDoEditor } from "@/api/editorDeLigacoes"
import { CampoDeBusca, idDe, textoDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"
import { TIPOS_PINOUT } from "../../tipos"
import { CampoDaPonta, pontaVazia, type PontaNaTela } from "./FormularioDoFio"
import {
  faltasDaJuncao, nomesDoRamo, operacaoDaJuncao, problemaNaBase, sugerirRamo, verificar,
  type Escolha, type LadoDaJuncao, type RamoNaTela,
} from "./regras"

type RamoNoAssistente = {
  chave: number
  ramo: string
  pinoDaJuncao: string
  conector: ValorBusca
  cor: ValorBusca
  modo: "novo" | "desenho"
  pontaDoDesenho: NoDoGrafo | null
  pontaNova: PontaNaTela
}

const escolha = (v: ValorBusca): Escolha => ({ id: idDe(v), texto: textoDe(v) })
const novoRamo = (chave: number, ramo: string): RamoNoAssistente => ({
  chave, ramo, pinoDaJuncao: "", conector: VAZIO, cor: VAZIO, modo: "novo", pontaDoDesenho: null, pontaNova: pontaVazia(chave),
})

export function AssistenteDeJuncao({
  projetoId, grafo, componente, restricaoId, restricaoRotulo, fonte, aoPedirPino, aoPorNoLote, aoCancelar,
}: {
  projetoId: number
  grafo: GrafoDoPinout
  /** A junção num componente que já está na tela; sem ele, o operador busca ou cria. */
  componente: { id: number; codigo: string } | null
  restricaoId: number | null
  restricaoRotulo: string
  fonte: string
  /** Pede ao editor o próximo pino clicado no desenho. */
  aoPedirPino: (quando: (no: NoDoGrafo) => void) => void
  aoPorNoLote: (op: OperacaoDoEditor, texto: string) => void
  aoCancelar: () => void
}) {
  const busca = usarBuscaDeComponentes(projetoId)
  const cores = usarCores(projetoId)
  const chicotes = usarChicotes(projetoId)
  const [comp, setComp] = useState<ValorBusca>(componente ? { estado: "vinculado", id: componente.id, texto: componente.codigo } : VAZIO)
  const conectores = usarConectores(idDe(comp) > 0 ? projetoId : 0, idDe(comp) || undefined)
  const [conector, setConector] = useState<ValorBusca>(VAZIO)
  const [baseJ, setBaseJ] = useState("")
  const [chicote, setChicote] = useState<ValorBusca>(VAZIO)
  const [tipoPino, setTipoPino] = useState(0)
  const [ramos, setRamos] = useState<RamoNoAssistente[]>(() => [novoRamo(1, "E"), novoRamo(2, "E1")])

  const lado: LadoDaJuncao = {
    componente: escolha(comp), conector: escolha(conector), base: baseJ, chicote: escolha(chicote), tipoPino, fonte, restricaoId,
  }
  const ramosNaTela: RamoNaTela[] = ramos.map((r) => ({
    ramo: r.ramo,
    pinoDaJuncao: r.pinoDaJuncao,
    conector: escolha(r.conector),
    cor: escolha(r.cor),
    pontaPinoutId: r.modo === "desenho" ? r.pontaDoDesenho?.pinoutId ?? 0 : 0,
    pontaNova: r.modo === "novo"
      ? { componente: escolha(r.pontaNova.componente), tipoComponente: r.pontaNova.tipoComponente, conector: escolha(r.pontaNova.conector), pino: r.pontaNova.pino }
      : null,
  }))
  const op = operacaoDaJuncao(lado, ramosNaTela)
  const faltas = faltasDaJuncao(lado, ramosNaTela)
  // O laço só depende das pontas: aparece assim que as duas forem escolhidas, antes do resto.
  const laco = verificar(grafo, op)
  const baseOk = baseJ.trim() !== "" && problemaNaBase(baseJ) === null

  function mudar(chave: number, m: (r: RamoNoAssistente) => RamoNoAssistente) {
    setRamos((l) => l.map((r) => (r.chave === chave ? m(r) : r)))
  }

  return (
    <section aria-label="Nova junção" style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div>
        <b style={{ fontSize: 13 }}>Nova junção</b>
        <div style={{ fontSize: 12, color: "var(--txt2)" }}>
          Diga onde fica a junção, a base do nome do fio e os ramos. O sistema escreve os nomes com # e marca o
          componente como Junção — ninguém digita #.
        </div>
      </div>

      {componente ? (
        <span style={{ fontSize: 12 }}>Componente da junção <b>{componente.codigo}</b></span>
      ) : (
        <CampoDeBusca
          rotulo="Componente da junção"
          valor={comp}
          aoMudar={(v) => { setComp(v); setConector(VAZIO) }}
          fonte={{ modo: "servidor", itens: busca.itens, carregando: busca.carregando, aoBuscar: busca.buscar }}
          mono
          dica="código (novo nasce como Junção)"
          id="juncao-componente"
        />
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <CampoDeBusca
          rotulo="Conector da junção"
          valor={conector}
          aoMudar={setConector}
          fonte={{ modo: "memoria", itens: conectores.itens, carregando: idDe(comp) > 0 && conectores.carregando }}
          mono
          dica="descrição"
          id="juncao-conector"
        />
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={rotulo}>Base do nome do fio</span>
          <input
            className="campo"
            value={baseJ}
            onChange={(e) => setBaseJ(e.target.value)}
            placeholder="ex.: E46T36"
            aria-label="Base do nome do fio"
            style={{ ...campo, fontFamily: "var(--fm)" }}
          />
        </label>
        <CampoDeBusca
          rotulo="Chicote"
          valor={chicote}
          aoMudar={setChicote}
          fonte={{ modo: "memoria", itens: chicotes.itens, carregando: chicotes.carregando }}
          id="juncao-chicote"
        />
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={rotulo}>Tipo do pino</span>
          <select className="campo" value={tipoPino} onChange={(e) => setTipoPino(Number(e.target.value))} style={campo} aria-label="Tipo do pino da junção">
            {Object.entries(TIPOS_PINOUT).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
          </select>
        </label>
      </div>

      {ramos.map((r) => {
        const nomes = baseOk && r.ramo.trim() ? nomesDoRamo(baseJ, r.ramo) : null
        const leva = r.modo === "desenho" && r.pontaDoDesenho && nomes && r.pontaDoDesenho.circuito.trim().toLowerCase() !== nomes.naPonta.toLowerCase()
          ? r.pontaDoDesenho
          : null
        const junto = leva ? grafo.nos.filter((n) => n.circuitoId === leva.circuitoId && n.id !== leva.id).length : 0
        return (
          <fieldset key={r.chave} style={{ border: "1px solid var(--line)", borderRadius: 7, padding: "7px 9px 9px", margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            <legend style={{ fontSize: 11.5, fontWeight: 700, color: "var(--txt2)", padding: "0 4px" }}>
              Ramo {r.ramo || "?"}
              {ramos.length > 2 && (
                <button type="button" onClick={() => setRamos((l) => l.filter((x) => x.chave !== r.chave))} title="Tirar este ramo"
                        style={{ marginLeft: 6, border: "none", background: "none", color: "var(--txt3)", cursor: "pointer" }}>×</button>
              )}
            </legend>
            <div style={{ display: "grid", gridTemplateColumns: "70px 80px 1fr", gap: 6 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={rotulo}>Ramo</span>
                <input className="campo" value={r.ramo} onChange={(e) => mudar(r.chave, (x) => ({ ...x, ramo: e.target.value }))}
                       aria-label={`Ramo ${r.chave}: nome`} style={{ ...campo, fontFamily: "var(--fm)" }} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={rotulo}>Pino na junção</span>
                <input className="campo" value={r.pinoDaJuncao} onChange={(e) => mudar(r.chave, (x) => ({ ...x, pinoDaJuncao: e.target.value }))}
                       aria-label={`Ramo ${r.chave}: pino na junção`} style={{ ...campo, fontFamily: "var(--fm)" }} />
              </label>
              <CampoDeBusca
                rotulo="Cor"
                valor={r.cor}
                aoMudar={(v) => mudar(r.chave, (x) => ({ ...x, cor: v }))}
                fonte={{ modo: "memoria", itens: cores.itens, carregando: cores.carregando }}
                id={`juncao-ramo-${r.chave}-cor`}
              />
            </div>
            <CampoDeBusca
              rotulo="Conector na junção (se não for o de cima)"
              valor={r.conector}
              aoMudar={(v) => mudar(r.chave, (x) => ({ ...x, conector: v }))}
              fonte={{ modo: "memoria", itens: conectores.itens, carregando: false }}
              mono
              id={`juncao-ramo-${r.chave}-conector`}
            />
            <div role="radiogroup" aria-label={`Ramo ${r.chave}: ponta`} style={{ display: "flex", gap: 12, fontSize: 12 }}>
              <label><input type="radio" checked={r.modo === "novo"} onChange={() => mudar(r.chave, (x) => ({ ...x, modo: "novo" }))} /> ponta nova</label>
              <label><input type="radio" checked={r.modo === "desenho"} onChange={() => mudar(r.chave, (x) => ({ ...x, modo: "desenho" }))} /> pino que já está no desenho</label>
            </div>
            {r.modo === "novo" ? (
              <CampoDaPonta
                projetoId={projetoId}
                ponta={r.pontaNova}
                titulo={`Ponta do ramo ${r.ramo || r.chave}`}
                fixo={false}
                podeTirar={false}
                aoMudar={(m) => mudar(r.chave, (x) => ({ ...x, pontaNova: m(x.pontaNova) }))}
                aoTirar={() => {}}
              />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
                <button
                  type="button"
                  onClick={() => aoPedirPino((no) => mudar(r.chave, (x) => ({ ...x, pontaDoDesenho: no })))}
                  style={{ alignSelf: "flex-start", height: 28, padding: "0 10px", borderRadius: 7, fontSize: 12, border: "1px solid var(--line2)", background: "var(--surface)", cursor: "pointer" }}
                >
                  {r.pontaDoDesenho ? "Trocar o pino…" : "Escolher no desenho…"}
                </button>
                {r.pontaDoDesenho && (
                  <span>
                    <b>{r.pontaDoDesenho.componenteCodigo || r.pontaDoDesenho.componente} {r.pontaDoDesenho.conector}·{r.pontaDoDesenho.pino}</b>
                    {" "}— fio <code style={{ fontFamily: "var(--fm)" }}>{r.pontaDoDesenho.circuito || "(vazio)"}</code>
                  </span>
                )}
                {leva && nomes && (
                  <span style={{ color: "var(--aviso-on-soft)" }}>
                    O fio '{leva.circuito}' passa a '{nomes.naPonta}'{junto > 0 ? ` e leva junto as outras ${junto} ponta(s) dele que estão no desenho` : ""}.
                  </span>
                )}
              </div>
            )}
          </fieldset>
        )
      })}
      <button
        type="button"
        onClick={() => setRamos((l) => [...l, novoRamo(Math.max(0, ...l.map((x) => x.chave)) + 1, sugerirRamo(l.map((x) => x.ramo)))])}
        style={{ alignSelf: "flex-start", height: 28, padding: "0 10px", borderRadius: 7, fontSize: 12, border: "1px dashed var(--line2)", background: "var(--surface)", color: "var(--txt2)", cursor: "pointer" }}
      >
        + mais um ramo
      </button>

      {baseOk && (
        <table aria-label="Os fios que serão gravados" style={{ fontSize: 11.5, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "var(--txt3)", textAlign: "left" }}>
              <th style={celula}>na ponta</th><th style={celula}>na junção</th>
            </tr>
          </thead>
          <tbody>
            {ramos.filter((r) => r.ramo.trim()).map((r) => {
              const n = nomesDoRamo(baseJ, r.ramo)
              return (
                <tr key={r.chave}>
                  <td style={{ ...celula, fontFamily: "var(--fm)" }}>{n.naPonta}</td>
                  <td style={{ ...celula, fontFamily: "var(--fm)" }}>{n.naJuncao}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
        Restrição: <b>{restricaoId === null ? "nenhuma escolhida" : restricaoId === 0 ? "Todos os modelos" : restricaoRotulo}</b>
        {" · "}Fonte: <b>{fonte.trim() || "—"}</b> (as duas no cabeçalho do editor)
      </span>
      {faltas.length > 0 && <span style={{ fontSize: 11.5, color: "var(--acao-on-soft)" }}>Falta: {faltas.join("; ")}.</span>}
      {laco && <div role="alert" style={{ fontSize: 12, padding: 8, borderRadius: 7, background: "var(--erro-soft)", color: "var(--erro-on-soft)" }}>{laco}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          disabled={faltas.length > 0 || laco !== null}
          onClick={() => aoPorNoLote(op,
            `junção nova ${baseJ.trim()} em ${textoDe(comp)}: ${ramos.map((r) => nomesDoRamo(baseJ, r.ramo).naJuncao).join(", ")}`)}
          style={{ ...botao, flex: 1, border: "none", background: "var(--brand)", color: "#FFFFFF", opacity: faltas.length > 0 || laco ? 0.5 : 1 }}
        >
          Pôr no lote: junção com {ramos.length} ramos
        </button>
        <button type="button" onClick={aoCancelar} style={{ ...botao, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--txt2)" }}>
          Cancelar
        </button>
      </div>
    </section>
  )
}

const rotulo: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }
const campo: React.CSSProperties = { height: 30, padding: "0 9px", borderRadius: 7, border: "1px solid var(--line2)", fontSize: 12.5, background: "var(--surface)" }
const botao: React.CSSProperties = { minHeight: 32, padding: "6px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }
const celula: React.CSSProperties = { padding: "3px 6px", borderBottom: "1px solid var(--line)" }
